import { Link } from "@inertiajs/react"
import { useState } from "react"

interface UserOption {
  id: number
  name: string
}

interface ContactOption {
  id: number
  name: string
}

interface ScheduleFormData {
  title: string
  notes: string
  start_date: string | null
  end_date: string | null
  no_start: boolean
  no_end: boolean
  leader_id: number | null
  customer_user_id: number | null
  investigator_ids: number[]
}

interface Props {
  customerId: number
  data: ScheduleFormData
  setData: (
    key: keyof ScheduleFormData,
    value: string | number | null | boolean | number[],
  ) => void
  errors?: Record<string, string[]>
  processing: boolean
  onSubmit: (e: React.FormEvent) => void
  submitLabel: string
  leaders: UserOption[]
  investigators: UserOption[]
  contacts: ContactOption[]
}

export default function ScheduleForm({
  customerId,
  data,
  setData,
  errors,
  processing,
  onSubmit,
  submitLabel,
  leaders,
  investigators,
  contacts,
}: Props) {
  // Start date/time are edited as two inputs but stored as a single composed
  // `start_date` string. When the time is unknown, the date is kept and the
  // time is stored as noon (12:00) — timezone-safe — while the flag drives
  // "TBD" display.
  const [startDate, setStartDate] = useState<string>(
    data.start_date ? (data.start_date.split("T")[0] ?? "") : "",
  )
  const [startTime, setStartTime] = useState<string>(
    data.start_date && !data.no_start
      ? (data.start_date.split("T")[1] ?? "")
      : "",
  )

  const composeStart = (
    date: string,
    time: string,
    noStart: boolean,
  ): string | null => {
    if (!date) return null
    return `${date}T${noStart ? "12:00" : time || "00:00"}`
  }

  const handleStartDateChange = (value: string) => {
    setStartDate(value)
    setData("start_date", composeStart(value, startTime, data.no_start))
  }

  const handleStartTimeChange = (value: string) => {
    setStartTime(value)
    setData("start_date", composeStart(startDate, value, data.no_start))
  }

  const handleNoStartChange = (checked: boolean) => {
    setData("no_start", checked)
    if (checked) setStartTime("")
    setData(
      "start_date",
      composeStart(startDate, checked ? "" : startTime, checked),
    )
  }

  const toggleInvestigator = (id: number) => {
    const ids = data.investigator_ids.includes(id)
      ? data.investigator_ids.filter((i) => i !== id)
      : [...data.investigator_ids, id]
    setData("investigator_ids", ids)
  }

  return (
    <form onSubmit={onSubmit} className="max-w-3xl">
      {errors?.base && (
        <div className="text-hpi-red mb-4 rounded bg-red-50 p-3 text-sm">
          {errors.base.join(", ")}
        </div>
      )}

      <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label htmlFor="start_date" className="mb-1 block font-medium">
            Start Date/Time
          </label>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="date"
              id="start_date"
              aria-label="Start date"
              value={startDate}
              onChange={(e) => handleStartDateChange(e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              required
            />
            <input
              type="time"
              aria-label="Start time"
              value={data.no_start ? "" : startTime}
              onChange={(e) => handleStartTimeChange(e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2 disabled:opacity-50"
              disabled={data.no_start}
              required={!data.no_start}
            />
          </div>
          <label className="mt-2 flex items-center gap-2">
            <input
              type="checkbox"
              checked={data.no_start}
              onChange={(e) => handleNoStartChange(e.target.checked)}
            />
            <span className="text-sm">Start time is unknown</span>
          </label>
          {errors?.start_date && (
            <p className="text-hpi-red mt-1 text-sm">
              {errors.start_date.join(", ")}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="end_date" className="mb-1 block font-medium">
            End Date/Time
          </label>
          <input
            type="datetime-local"
            id="end_date"
            value={data.no_end ? "" : (data.end_date ?? "")}
            onChange={(e) => setData("end_date", e.target.value || null)}
            className="border-hpi-light-gray w-full rounded border px-3 py-2 disabled:opacity-50"
            disabled={data.no_end}
          />
          <label className="mt-2 flex items-center gap-2">
            <input
              type="checkbox"
              checked={data.no_end}
              onChange={(e) => {
                setData("no_end", e.target.checked)
                if (e.target.checked) setData("end_date", null)
              }}
            />
            <span className="text-sm">End time is unknown</span>
          </label>
          {errors?.end_date && (
            <p className="text-hpi-red mt-1 text-sm">
              {errors.end_date.join(", ")}
            </p>
          )}
        </div>
      </div>

      {contacts.length > 0 && (
        <div className="mb-4">
          <label htmlFor="customer_user_id" className="mb-1 block font-medium">
            Contact
          </label>
          <select
            id="customer_user_id"
            value={data.customer_user_id ?? ""}
            onChange={(e) =>
              setData(
                "customer_user_id",
                e.target.value ? parseInt(e.target.value) : null,
              )
            }
            className="border-hpi-light-gray w-full rounded border px-3 py-2"
          >
            <option value="">Choose...</option>
            {contacts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="mb-4">
        <label htmlFor="leader_id" className="mb-1 block font-medium">
          Team Leader
        </label>
        <select
          id="leader_id"
          value={data.leader_id ?? ""}
          onChange={(e) =>
            setData(
              "leader_id",
              e.target.value ? parseInt(e.target.value) : null,
            )
          }
          className="border-hpi-light-gray w-full rounded border px-3 py-2"
        >
          <option value="">Choose...</option>
          {leaders.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-4">
        <label htmlFor="title" className="mb-1 block font-medium">
          Title
        </label>
        <input
          type="text"
          id="title"
          value={data.title}
          onChange={(e) => setData("title", e.target.value)}
          className="border-hpi-light-gray w-full rounded border px-3 py-2"
          maxLength={50}
        />
        {errors?.title && (
          <p className="text-hpi-red mt-1 text-sm">{errors.title.join(", ")}</p>
        )}
      </div>

      <div className="mb-4">
        <label htmlFor="notes" className="mb-1 block font-medium">
          Description
        </label>
        <textarea
          id="notes"
          value={data.notes}
          onChange={(e) => setData("notes", e.target.value)}
          className="border-hpi-light-gray w-full rounded border px-3 py-2"
          rows={4}
        />
        {errors?.notes && (
          <p className="text-hpi-red mt-1 text-sm">{errors.notes.join(", ")}</p>
        )}
      </div>

      <div className="mb-6">
        <label className="mb-2 block font-medium">Attendees</label>
        {investigators.length > 0 ? (
          <div className="border-hpi-light-gray max-h-60 space-y-1 overflow-y-auto rounded border p-3">
            {investigators.map((inv) => (
              <label key={inv.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={data.investigator_ids.includes(inv.id)}
                  onChange={() => toggleInvestigator(inv.id)}
                />
                <span className="text-sm">{inv.name}</span>
              </label>
            ))}
          </div>
        ) : (
          <p className="text-hpi-med-gray text-sm">No employees available.</p>
        )}
      </div>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={processing}
          className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
        >
          {submitLabel}
        </button>
        <Link
          href={`/customers/${customerId}/schedules`}
          className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
        >
          Cancel
        </Link>
      </div>
    </form>
  )
}
